import fetchWrapper from '../fetchWrapper';
import { ProductOption } from '../storeService';

const BASE_URL = process.env.BASE_URL;

export interface CartItemOption {
    id: number;
    name: string;
    price: number;
    qty: number;
    is_active?: boolean;
}

export interface CartItemProduct {
    id: number;
    // false 면 전자책(배송 없음)
    requires_shipping?: boolean;
    name: string;
    description: string | null;
    categories: Array<{ id: number | string; name: string; type: string }>;
    is_active: boolean;
    status: number;
    qty: number;
    point: number | null;
    price: number | null;
    regular_price: number | null;
    sale_price: number | null;
    type: string;
    is_store: boolean;
    is_point: boolean;
    is_gift: boolean;
    sold_qty: number;
    view: number;
    review_count: number;
    rating_average: number;
    images: Array<{
        id: number;
        product_id: number;
        media_type: string;
        media_url: string;
        media_lg_url: string;
        media_md_url: string;
        media_sm_url: string;
        order: number;
        created_at: string;
    }>;
    created_at: string;
    delivery_fee: number;
    original_delivery_fee: number;
}

export interface CartItem {
    id: number;
    product: CartItemProduct;
    option: ProductOption;
    quantity: number;
    created_at: string;
    updated_at: string;
    is_selected: boolean;
    unit_original_price: number;
    unit_discount_value: number;
    unit_price_after_discount: number;
    total_original_price: number;
    total_discount_value: number;
    total_price_after_discount: number;
}

export interface CartSummary {
    total_quantity: number;
    total_original_price: number;
    total_discount_value: number;
    total_price_after_discount: number;
    delivery_fee: number;
    original_delivery_fee: number;
    free_shipping_threshold: number;
    final_price: number;
}

export interface CartData {
    items: CartItem[];
    summary: CartSummary;
}

export interface CartResponse {
    success: boolean;
    message: string;
    data: CartData;
}

export interface AddToCartItem {
    product_id: number;
    product_option_id: number | null;
    quantity: number;
}

class CartService {
    async getCart(): Promise<CartData> {
        try {
            const url = `${BASE_URL}/cart`;
            const response = await fetchWrapper.get(url);
            const cartResponse = response as unknown as CartResponse;
            return cartResponse.data || { items: [], summary: { total_quantity: 0, total_original_price: 0, total_discount_value: 0, total_price_after_discount: 0, delivery_fee: 0, original_delivery_fee: 0, free_shipping_threshold: 0, final_price: 0 } };
        } catch (error) {
            console.error('Error fetching cart:', error);
            throw error;
        }
    }

    async addToCart(items: AddToCartItem[]): Promise<any> {
        try {
            const response = await fetchWrapper.post(`${BASE_URL}/cart`, { items });
            return response;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    }

    async updateCartItemQuantity(id: number, quantity: number): Promise<CartData> {
        try {
            const body: Record<string, any> = {
                _method: 'PATCH',
                quantity,
            };
            const response = await fetchWrapper.post(`${BASE_URL}/cart/${id}`, body);
            const apiBody = response.data as unknown as CartResponse;
            return apiBody.data || { items: [], summary: { total_quantity: 0, total_original_price: 0, total_discount_value: 0, total_price_after_discount: 0, delivery_fee: 0, original_delivery_fee: 0, free_shipping_threshold: 0, final_price: 0 } };
        } catch (error) {
            console.error('Error updating cart item quantity:', error);
            throw error;
        }
    }

    async updateCartItemSelection(id: number, isSelected: boolean): Promise<CartData> {
        try {
            const body: Record<string, any> = {
                _method: 'PATCH',
                is_selected: isSelected ? 1 : 0,
            };
            const response = await fetchWrapper.post(`${BASE_URL}/cart/${id}`, body);
            const apiBody = response.data as unknown as CartResponse;
            return apiBody.data || { items: [], summary: { total_quantity: 0, total_original_price: 0, total_discount_value: 0, total_price_after_discount: 0, delivery_fee: 0, original_delivery_fee: 0, free_shipping_threshold: 0, final_price: 0 } };
        } catch (error) {
            console.error('Error updating cart item selection:', error);
            throw error;
        }
    }

    async updateAllCartSelection(isSelected: boolean): Promise<CartData> {
        try {
            const body: Record<string, any> = {
                _method: 'PATCH',
                is_selected: isSelected ? 1 : 0,
            };
            const response = await fetchWrapper.post(`${BASE_URL}/cart/selection`, body);
            const apiBody = response.data as unknown as CartResponse;
            return apiBody.data || { items: [], summary: { total_quantity: 0, total_original_price: 0, total_discount_value: 0, total_price_after_discount: 0, delivery_fee: 0, original_delivery_fee: 0, free_shipping_threshold: 0, final_price: 0 } };
        } catch (error) {
            console.error('Error updating all cart selection:', error);
            throw error;
        }
    }

    async deleteCartItem(id: number): Promise<any> {
        try {
            const response = await fetchWrapper.delete(`${BASE_URL}/cart/${id}`);
            return response;
        } catch (error) {
            console.error('Error deleting cart item:', error);
            throw error;
        }
    }

    async deleteAllCartItems(): Promise<any> {
        try {
            const response = await fetchWrapper.delete(`${BASE_URL}/cart`);
            return response;
        } catch (error) {
            console.error('Error deleting all cart items:', error);
            throw error;
        }
    }
}

const cartService = new CartService();
export default cartService;
